import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, AlertTriangle, Lightbulb, TrendingUp, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import SiteLayout from "@/components/site/SiteLayout";
import { fetchCase, toAbsolute } from "@/lib/api";
import ShareButtons from "@/components/site/ShareButtons";

export default function CaseStudy() {
  const { slug } = useParams();
  const { t, dir } = useLang();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const Back = dir === "rtl" ? ArrowRight : ArrowLeft;

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCase(slug).then((r) => setItem(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const blocks = item ? [
    { icon: AlertTriangle, label: t.cases.problem, text: item.problem, color: "#f59e0b" },
    { icon: Lightbulb, label: t.cases.solution, text: item.solution, color: "#00F260" },
    { icon: TrendingUp, label: t.cases.result, text: item.result, color: "#38bdf8" },
  ] : [];

  return (
    <SiteLayout>
      {notFound ? (
        <div className="min-h-[50vh] grid place-items-center text-slate-400">404 — {t.cases.empty}</div>
      ) : !item ? (
        <div className="min-h-[50vh] grid place-items-center"><Loader2 className="animate-spin text-primary" /></div>
      ) : (
        <article className="pb-24" dir="auto" data-testid="case-detail">
          <div className="relative h-[45vh] min-h-[320px] overflow-hidden">
            <img src={toAbsolute(item.cover_image)} alt={item.title} className="h-full w-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,9,20,0.4), var(--bg))" }} />
            <div className="absolute bottom-0 inset-x-0">
              <div className="mx-auto max-w-4xl px-5 sm:px-8 pb-8">
                <div className="flex flex-wrap gap-3 mb-3 text-sm">
                  {item.client && <span className="text-slate-300">{t.cases.client}: <b className="text-white">{item.client}</b></span>}
                  {item.sector && <span className="text-slate-300">{t.cases.sector}: <b className="text-primary">{item.sector}</b></span>}
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">{item.title}</h1>
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-4xl px-5 sm:px-8 mt-10">
            <p className="text-xl text-slate-300 leading-relaxed mb-10">{item.summary}</p>

            <div className="space-y-5">
              {blocks.map((b, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="card-surface rounded-2xl p-7 flex gap-5"
                >
                  <div className="h-12 w-12 rounded-2xl grid place-items-center shrink-0" style={{ background: `${b.color}22` }}>
                    <b.icon size={22} style={{ color: b.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-2">{b.label}</h3>
                    <p className="text-slate-400 leading-relaxed text-lg">{b.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {item.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8">
                {item.tags.map((tag) => (
                  <span key={tag} className="rounded-full border border-white/10 px-4 py-1.5 text-sm text-slate-300">{tag}</span>
                ))}
              </div>
            )}

            <Link to="/case-studies" className="mt-12 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-bold hover:bg-white/5 transition-colors" data-testid="case-back">
              <Back size={16} /> {t.cases.back}
            </Link>
            <div className="mt-10 pt-6 border-t border-white/10">
              <ShareButtons title={item.title} testid="case-share" shareType="case-studies" shareId={item.slug} />
            </div>
          </div>
        </article>
      )}
    </SiteLayout>
  );
}
